import { useEffect, useMemo, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { VscEye, VscEyeClosed } from "react-icons/vsc";
import { FaRegCircleCheck } from "react-icons/fa6";
import { IoIosCloseCircleOutline } from "react-icons/io";

interface PasswordInputProps {
  name: string;
  value: string;
  label?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputError?: string;
  setInputError?: (error: string) => void;
  isShowChecks?: boolean;
}

const PasswordInput = ({
  name,
  value,
  label,
  onChange,
  inputError,
  setInputError,
  isShowChecks,
}: PasswordInputProps) => {
  const { _t } = useTranslator();
  const [focused, setFocused] = useState(false);
  const [allPassed, setAllPassed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const passwordChecks = useMemo(() => {
    const pwd = value || "";
    return {
      length: pwd.length >= 8,
      upper: /[A-Z]/.test(pwd),
      lower: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[!@#$%^&*(),.?":{}|<>_]/.test(pwd),
    };
  }, [value]);

  useEffect(() => {
    const passed = Object.values(passwordChecks).every(Boolean);

    if (passed && focused) {
      setAllPassed(true);
      setFocused(false);
    } else if (!passed && allPassed) {
      setAllPassed(false);

      const activeElement = document.activeElement as HTMLInputElement | null;
      if (activeElement?.name === name) {
        setFocused(true);
      }
    }
  }, [passwordChecks, focused, allPassed, name]);

  const renderCheck = (condition: boolean, text: string) => (
    <div className={`rule-item ${condition ? "passed" : ""}`}>
      {condition ? (
        <FaRegCircleCheck size={14} />
      ) : (
        <IoIosCloseCircleOutline size={14} />
      )}
      <span className="rule-text">{text}</span>
    </div>
  );

  return (
    <div className="main-form-group">
      <div
        className={`form-group ${
          name === "confirmPassword" && inputError ? "form-group-error" : ""
        }`}
      >
        <label>{label ?? _t("password")}</label>
        <div className="d-flex justify-content-between align-item-center">
          <input
            className="w-100"
            type={showPassword ? "text" : "password"}
            name={name}
            placeholder={_t("enterPassword", "Text used in onboarding process")}
            value={value}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(e) => {
              onChange(e);
              if (inputError) {
                setInputError?.("");
              }
            }}
            required
          />
          {showPassword ? (
            <VscEye
              className="eye-icons cursor-pointer"
              onClick={() => setShowPassword(false)}
            />
          ) : (
            <VscEyeClosed
              className="eye-icons cursor-pointer"
              onClick={() => setShowPassword(true)}
            />
          )}
        </div>
        {focused && isShowChecks && (
          <div className="errorPopup">
            {renderCheck(passwordChecks.upper, _t("atLeastOneUpperCaseLetter"))}
            {renderCheck(passwordChecks.lower, _t("atLeastOneLowerCaseLetter"))}
            {renderCheck(passwordChecks.number, _t("atLeastOneNumber"))}
            {renderCheck(passwordChecks.special, _t("atLeastOneSpecialLetter"))}
            {renderCheck(
              passwordChecks.length,
              _t("minimumEightCharactersLong")
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordInput;
